/**
 * LocalizedDateInput - Date input with locale-aware format display
 * Displays and accepts dates in user's preferred format (yyyy-mm-dd, dd/mm/yyyy, mm/dd/yyyy)
 * Outputs ISO format (yyyy-mm-dd) for backend/form storage
 *
 * The calendar button opens the shared design-system `Calendar`
 * (shadcn/react-day-picker), NOT the browser's own `showPicker()` on a hidden
 * `<input type="date">`. Two things the native popup cannot do: it is unthemed, so it
 * ignores dark mode and the tenant theme, and it is localized to the BROWSER's locale
 * and week-start rather than the viewer's own preferences — so a user whose PanLuma
 * language is Hebrew got an English, Sunday-first, left-to-right calendar in the
 * middle of an otherwise translated form, with the field beneath it still rendering
 * the date in their configured order. `Calendar` resolves language, week-start and
 * direction from `UserPreferencesContext`, which is the same source this field's own
 * `dateFormat` comes from.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericFieldBox } from './common/NumericFieldBox';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
// The concrete module, not the `../context` barrel: this component is composed into
// `LocalizedDateTimeInput`, so it now renders inside specs that partially mock the
// barrel for some OTHER context — and a partial mock of a barrel has no
// `useUserPreferences` on it, which crashes the render.
import { useUserPreferences } from '../context/UserPreferencesContext';

export interface LocalizedDateInputProps {
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Fires when the field starts or stops showing its own validation error.
   *
   * A rejected date keeps the text the user typed but is deliberately NOT sent up
   * through `onChange`, so `value` still holds the last accepted date. A form that
   * can submit needs this to notice the divergence — otherwise the user reads
   * "07/15/2026" in a red field and the submit sends the older date underneath it.
   */
  onValidityChange?: (isValid: boolean) => void;
  minDate?: string;
  maxDate?: string;
  label?: string;
  /**
   * Accessible name for the text field, for a caller that renders the visible label
   * itself — `LocalizedDateTimeInput`, whose one label names a two-field group and so
   * cannot also name this half of it.
   */
  ariaLabel?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  /** Applied to the wrapper. */
  className?: string;
  /**
   * Applied to the text field itself. For inline table editors that render
   * view-first (`border-transparent hover:border-input`) alongside their
   * neighbouring cells — the wrapper class can't reach the input.
   */
  inputClassName?: string;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

// Parse user input based on format preference
function parseUserDate(input: string, format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string | null {
  if (!input || input.trim() === '') return null;

  const cleaned = input.trim();
  let year: number, month: number, day: number;

  if (format === 'yyyy-mm-dd') {
    const match = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    [, year, month, day] = match.map(Number) as [unknown, number, number, number];
  } else if (format === 'dd/mm/yyyy') {
    const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    [, day, month, year] = match.map(Number) as [unknown, number, number, number];
  } else {
    // mm/dd/yyyy
    const match = cleaned.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;
    [, month, day, year] = match.map(Number) as [unknown, number, number, number];
  }

  // Validate date components
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  if (year < 1900 || year > 2100) return null;

  // Validate actual date (handles month lengths, leap years)
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  // Return ISO format
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Format ISO date for user display
function formatForDisplay(isoDate: string | null, format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string {
  if (!isoDate) return '';

  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';

  const [, year, month, day] = match;

  if (format === 'yyyy-mm-dd') {
    return `${year}-${month}-${day}`;
  } else if (format === 'dd/mm/yyyy') {
    return `${day}/${month}/${year}`;
  } else {
    return `${month}/${day}/${year}`;
  }
}

// Get placeholder based on format
function getPlaceholder(format: 'yyyy-mm-dd' | 'dd/mm/yyyy' | 'mm/dd/yyyy'): string {
  return format.toUpperCase();
}

/**
 * ISO `YYYY-MM-DD` → a `Date` at LOCAL midnight, which is the calendar's own idiom.
 *
 * Deliberately not `new Date(iso)`: the spec parses a bare `YYYY-MM-DD` as UTC
 * midnight, so west of Greenwich `getDate()` reports the day BEFORE — the calendar
 * would highlight, and return, the wrong cell. Building from parts keeps the value a
 * pure calendar date with no zone in play, which is what a date-only field means.
 */
function isoToCalendarDate(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return undefined;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/** The inverse: a local `Date`'s calendar day as `YYYY-MM-DD`. */
function calendarDateToIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function LocalizedDateInput({
  value,
  onChange,
  onValidityChange,
  minDate,
  maxDate,
  label,
  ariaLabel,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  inputClassName,
  id,
  onBlur: externalOnBlur,
}: LocalizedDateInputProps) {
  const { t } = useTranslation('common');
  const { formatOptions } = useUserPreferences();
  const dateFormat = formatOptions.dateFormat;
  const generatedId = React.useId();

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value, dateFormat));
  const [validationError, setValidationError] = useState<string | null>(null);

  const [calendarOpen, setCalendarOpen] = useState(false);

  // Resync the display only when `value` or the format changes from OUTSIDE.
  //
  // The previous implementation derived this from `displayValue` and reran on every
  // keystroke, which made a rejected entry vanish: a min/max violation deliberately
  // withholds `onChange`, so `value` stayed put, the effect saw the mismatch, and it
  // wiped the field the user had just typed — with no message. Comparing against the
  // last synced pair instead means the user's own typing never triggers a resync,
  // while a parent-driven reset (dialog close, form reset) still does.
  const syncedRef = useRef({ value, dateFormat });
  useEffect(() => {
    if (syncedRef.current.value === value && syncedRef.current.dateFormat === dateFormat) return;
    syncedRef.current = { value, dateFormat };
    setDisplayValue(formatForDisplay(value, dateFormat));
    setValidationError(null);
  }, [value, dateFormat]);

  // Read through a ref so a caller passing an inline arrow doesn't have to memoize
  // it to avoid an extra notification on every render.
  const validityListenerRef = useRef(onValidityChange);
  validityListenerRef.current = onValidityChange;
  useEffect(() => {
    validityListenerRef.current?.(validationError === null);
  }, [validationError]);

  // Build input mask pattern based on format
  const inputPattern = useMemo(() => {
    if (dateFormat === 'yyyy-mm-dd') {
      return /^[\d-]*$/;
    }
    return /^[\d/]*$/;
  }, [dateFormat]);

  // Message for a date the user typed correctly but that falls outside the allowed
  // range — returns null when the date is acceptable.
  const boundsMessage = useCallback((iso: string): string | null => {
    if (minDate && iso < minDate) {
      return t('validation.dateNotBefore', { date: formatForDisplay(minDate, dateFormat) });
    }
    if (maxDate && iso > maxDate) {
      return t('validation.dateNotAfter', { date: formatForDisplay(maxDate, dateFormat) });
    }
    return null;
  }, [minDate, maxDate, dateFormat, t]);

  const formatMessage = useCallback(
    () => t('validation.dateFormat', { format: getPlaceholder(dateFormat) }),
    [t, dateFormat]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow empty input
    if (input === '') {
      setDisplayValue('');
      setValidationError(null);
      onChange(null);
      return;
    }

    // Only allow valid characters
    if (!inputPattern.test(input)) return;

    // Limit length (10 chars for full date)
    if (input.length > 10) return;

    setDisplayValue(input);

    // Only judge a date once it is long enough to be complete — flagging a
    // half-typed date would put an error under the field on the first keystroke.
    if (input.length < 10) {
      setValidationError(null);
      return;
    }

    const parsed = parseUserDate(input, dateFormat);
    if (!parsed) {
      setValidationError(formatMessage());
      return;
    }

    // A rejected date keeps the text the user typed; only `onChange` is withheld,
    // so the parent never stores a value outside its own bounds.
    const outOfRange = boundsMessage(parsed);
    setValidationError(outOfRange);
    if (outOfRange) return;

    onChange(parsed);
  }, [inputPattern, dateFormat, onChange, boundsMessage, formatMessage]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (displayValue === '') {
      setValidationError(null);
      onChange(null);
    } else {
      const parsed = parseUserDate(displayValue, dateFormat);
      if (!parsed) {
        setValidationError(formatMessage());
      } else {
        const outOfRange = boundsMessage(parsed);
        setValidationError(outOfRange);
        if (!outOfRange) {
          setDisplayValue(formatForDisplay(parsed, dateFormat));
          onChange(parsed);
        }
      }
    }

    externalOnBlur?.(e);
  }, [displayValue, dateFormat, onChange, externalOnBlur, boundsMessage, formatMessage]);

  // A day picked off the calendar is a complete, in-range date by construction — the
  // bounds reach the calendar as disabled matchers — so it clears any pending typing
  // error and closes the popover. One click, one decision.
  const handleCalendarSelect = useCallback((selected: Date | undefined) => {
    if (!selected) return;
    const iso = calendarDateToIso(selected);
    setDisplayValue(formatForDisplay(iso, dateFormat));
    setValidationError(null);
    onChange(iso);
    setCalendarOpen(false);
  }, [dateFormat, onChange]);

  const selectedDate = useMemo(() => isoToCalendarDate(value), [value]);

  // Bounds as matchers rather than as a message afterwards: a day the parent would
  // reject is not offerable at all, so the user never picks one only to be told.
  const disabledDays = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    const min = isoToCalendarDate(minDate);
    if (min) matchers.push({ before: min });
    const max = isoToCalendarDate(maxDate);
    if (max) matchers.push({ after: max });
    return matchers;
  }, [minDate, maxDate]);

  const inputId = id || `date-input-${generatedId}`;
  const messageId = `${inputId}-message`;
  const isError = validationError !== null || error === true;
  // The validation message takes the helper slot — one line under the field says
  // either what the field expects or what is wrong with it, never both.
  const message = validationError ?? helperText;

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        {/* A date is a digit+separator token, so the input's `dir="auto"` lays
            it out LTR — while the calendar trigger's logical `end-0` resolves
            against the RTL document and lands on top of it. Pin the box. See
            NumericFieldBox. */}
        <NumericFieldBox className="relative">
          <Input
            id={inputId}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder || getPlaceholder(dateFormat)}
            disabled={disabled}
            inputMode="numeric"
            aria-label={ariaLabel}
            aria-invalid={isError || undefined}
            aria-describedby={message ? messageId : undefined}
            className={cn(
              'pe-10',
              size === 'sm' && 'h-8 text-sm',
              isError && 'border-destructive focus-visible:ring-destructive',
              inputClassName
            )}
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              disabled={disabled}
              aria-label={t('app.openCalendar')}
            >
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
        </NumericFieldBox>
        {/* No X here, unlike the time picker's. This popover commits on ONE click,
            so a user who opened it by mistake is one click from done either way;
            the clock face needs two (hour, then minute) and can strand you between
            them. The `DatePicker` sibling has no X either. */}
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate}
            disabled={disabledDays}
            onSelect={handleCalendarSelect}
            autoFocus
          />
        </PopoverContent>
      </Popover>
      {message && (
        <p
          id={messageId}
          className={cn(
            'text-xs mt-1',
            isError ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
}
